import api from './api';

// Get all stories (with optional userId and appId filters)
const getStories = async (userId = null, appId = null) => {
  try {
    const params = {};
    if (userId) params.userId = userId.toString();
    if (appId) params.appId = appId.toString();
    const response = await api.get('/api/v1/story/getStories', { params });
    return response.data;
  } catch (error) {
    console.error('Get stories error:', error);
    throw error;
  }
};

// Get a specific story by ID
const getStoryById = async (storyId) => {
  try {
    const response = await api.get(`/api/v1/story/getStory/${storyId}`);
    return response.data;
  } catch (error) {
    console.error('Get story error:', error);
    throw error;
  }
};

// Create a new story
const createStory = async (text, circularImage, appId = null) => {
  try {
    // Create a new FormData instance
    const formData = new FormData();
    
    // Add text if provided
    if (text) {
      formData.append('text', text);
    }
    
    // Add appId if provided, ensuring it's a string
    if (appId) {
      formData.append('appId', appId.toString());
    }
    
    // Log file information for debugging
    console.log('File being uploaded:', {
      name: circularImage.name,
      type: circularImage.type,
      size: circularImage.size,
      lastModified: new Date(circularImage.lastModified).toISOString()
    });
    
    // Add the file with a specific name
    formData.append('circularImage', circularImage, circularImage.name);
    
    // Make the API request without specifying Content-Type
    // Let the browser set the appropriate multipart/form-data with boundary
    const response = await api.post('/api/v1/story/create', formData, {
      headers: {
        'Content-Type': 'multipart/form-data',
      },
    });
    
    return response.data;
  } catch (error) {
    console.error('Create new story error:', error);
    throw error;
  }
};

// Add an item to a story
const addStoryItem = async (storyId, mediaFile, mediaType, order, linkUrl = null, linkLabel = null, linkPositionX = null, linkPositionY = null) => {
  try {
    const formData = new FormData();
    // Add parameters as strings
    formData.append('storyId', storyId.toString());
    formData.append('mediaType', mediaType);
    formData.append('order', order.toString());
    
    // Add link information if provided
    if (linkUrl) {
      formData.append('linkUrl', linkUrl);
    }
    
    if (linkLabel) {
      formData.append('linkLabel', linkLabel);
    }
    
    if (linkPositionX !== null) {
      formData.append('linkPositionX', linkPositionX.toString());
    }
    
    if (linkPositionY !== null) {
      formData.append('linkPositionY', linkPositionY.toString());
    }
    
    // Log file information for debugging
    console.log('Media file being uploaded:', {
      name: mediaFile.name,
      type: mediaFile.type,
      size: mediaFile.size,
      lastModified: new Date(mediaFile.lastModified).toISOString()
    });
    
    // Add the file with a specific name
    formData.append('mediaFile', mediaFile, mediaFile.name);
    
    // Make the API request without specifying Content-Type
    const response = await api.post('/api/v1/story/addItem', formData, {
      headers: {
        'Content-Type': 'multipart/form-data',
      },
    });
    
    return response.data;
  } catch (error) {
    console.error('Add story item error:', error);
    throw error;
  }
};

// Delete a story
const deleteStory = async (storyId) => {
  try {
    const response = await api.delete(`/api/v1/story/delete/${storyId}`);
    return response.data;
  } catch (error) {
    console.error('Delete story error:', error);
    throw error;
  }
};

// Update story order
const updateStoryOrder = async (storyId, newOrder) => {
  try {
    const response = await api.post('/api/v1/story/updateOrder', {
      storyId,
      newOrder
    });
    return response.data;
  } catch (error) {
    console.error('Update story order error:', error);
    throw error;
  }
};

// Toggle story disabled state
const toggleStoryDisabled = async (storyId, isDisabled) => {
  try {
    const response = await api.post('/api/v1/story/toggleDisabled', {
      storyId,
      isDisabled
    });
    return response.data;
  } catch (error) {
    console.error('Toggle story disabled state error:', error);
    throw error;
  }
};

// Schedule story publishing with start and end times
const schedulePublish = async (storyId, startTime, endTime) => {
  try {
    const response = await api.post('/api/v1/story/schedulePublish', {
      storyId,
      startTime,
      endTime
    });
    return response.data;
  } catch (error) {
    console.error('Schedule story publishing error:', error);
    throw error;
  }
};

// Toggle story published state (one-way operation: draft -> published)
const toggleStoryPublished = async (storyId) => {
  try {
    const response = await api.post('/api/v1/story/togglePublished', {
      storyId
    });
    return response.data;
  } catch (error) {
    console.error('Toggle story published state error:', error);
    throw error;
  }
};

// Track a story view
const trackStoryView = async (storyId) => {
  try {
    const response = await api.post('/api/v1/story/trackView', {
      storyId
    });
    return response.data;
  } catch (error) {
    console.error('Track story view error:', error);
    throw error;
  }
};

// Track a story completion (watched until the end)
const trackStoryCompletion = async (storyId) => {
  try {
    const response = await api.post('/api/v1/story/trackCompletion', {
      storyId
    });
    return response.data;
  } catch (error) {
    console.error('Track story completion error:', error);
    throw error;
  }
};

// Get analytics data
const getAnalytics = async (appId, storyId = 'all') => {
  try {
    const params = { appId: appId.toString() };
    if (storyId !== 'all') {
      params.storyId = storyId.toString();
    }
    const response = await api.get('/api/v1/story/analytics', { params });
    return response.data;
  } catch (error) {
    console.error('Get analytics error:', error);
    throw error;
  }
};

const StoryService = {
  getStories,
  getStoryById,
  createStory,
  addStoryItem,
  deleteStory,
  updateStoryOrder,
  toggleStoryDisabled,
  toggleStoryPublished,
  schedulePublish,
  trackStoryView,
  trackStoryCompletion,
  getAnalytics
};

export default StoryService;